import { Plus, Trash2 } from 'lucide-react';
import { LINE_UNITS, type Money } from '@/api/types';
import { formatMoney } from '@/lib/format';
import { blankLine, lineProblem, lineTotalMinor, problemText, type LineDraft } from '@/lib/lines';

/**
 * Lines of an invoice or an act: title, description, quantity, unit, price. The sum of a line is
 * shown for orientation only: the BFF computes the totals from quantity x price.
 */
export function LinesEditor({
  lines,
  onChange,
  currency,
  allowFreePrice,
  showProblems,
  label = 'Строки',
}: {
  lines: LineDraft[];
  onChange: (lines: LineDraft[]) => void;
  currency: Money['currency'];
  /** Act lines may wait for a price (a task whose price the studio fills in by hand). */
  allowFreePrice?: boolean;
  showProblems?: boolean;
  label?: string;
}) {
  const set = (key: number, patch: Partial<LineDraft>) => onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  return (
    <fieldset>
      <legend className="mb-1.5 text-sm text-muted">{label}</legend>
      {lines.length === 0 && <p className="rounded px-3 py-4 text-sm text-muted shadow-hair">Строк пока нет. Добавьте свою или выберите готовую выше.</p>}
      <ul className="space-y-2">
        {lines.map((l, i) => {
          const problem = showProblems ? lineProblem(l, allowFreePrice) : null;
          return (
            <li key={l.key} className="rounded p-3 shadow-hair" aria-label={`Строка ${i + 1}`}>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-[1fr_72px_96px_112px_auto]">
                <input className="field h-9 py-0 text-sm col-span-3 sm:col-span-1" aria-label={`Название, строка ${i + 1}`} placeholder="Что сделано или оказано" maxLength={200} value={l.title} onChange={(e) => set(l.key, { title: e.target.value })} />
                <input className="field num h-9 py-0 text-right text-sm" inputMode="decimal" aria-label={`Количество, строка ${i + 1}`} placeholder="Кол-во" value={l.quantity} onChange={(e) => set(l.key, { quantity: e.target.value })} />
                <input className="field h-9 py-0 text-sm" list="line-units" aria-label={`Единица, строка ${i + 1}`} placeholder="Ед." maxLength={10} value={l.unit} onChange={(e) => set(l.key, { unit: e.target.value })} />
                <input className="field num h-9 py-0 text-right text-sm" inputMode="decimal" aria-label={`Цена, строка ${i + 1}`} placeholder="Цена" value={l.price} onChange={(e) => set(l.key, { price: e.target.value })} />
                <div className="col-span-3 flex items-center justify-end gap-2 sm:col-span-1">
                  <span className="num w-24 text-right text-sm">{formatMoney({ amountMinor: lineTotalMinor(l), currency })}</span>
                  <button type="button" className="btn-ghost size-8 px-0" aria-label={`Убрать строку ${i + 1}`} onClick={() => onChange(lines.filter((x) => x.key !== l.key))}>
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
              <input className="field mt-2 h-8 py-0 text-sm" aria-label={`Описание, строка ${i + 1}`} placeholder="Описание (необязательно)" maxLength={1000} value={l.description} onChange={(e) => set(l.key, { description: e.target.value })} />
              {problem && (
                <p className="mt-1 text-xs text-overdue" role="alert">
                  Строка {i + 1}: {problemText[problem]}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      <datalist id="line-units">
        {LINE_UNITS.map((u) => (
          <option key={u} value={u} />
        ))}
      </datalist>
      <button type="button" className="btn-ghost mt-2 h-8 px-2 text-sm" onClick={() => onChange([...lines, blankLine()])}>
        <Plus className="size-4" /> Добавить строку
      </button>
    </fieldset>
  );
}
